import React, { useState, useEffect } from 'react';
import {
  ButtonWapper,
  Heading,
  InfoButton,
  Main,
  SectionLeft,
  SectionRight,
  SubHeading,
  SliderToggle,
  PanelContent,
  CaptureButton,
} from './mapView.style';
import { Button } from '../../../components/uielements';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IntlMessages } from '../../../helper/langHelper';
import PropertyOverlaysModal from './modal/PropertyOverlays';
import { ArrowLeft, Info, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import MapWithOverlays from './mapWithOverlays';
import useOverlayStore from '../../../store/overlayStore';
// import MultipleScreenshotModal from './modal/multipleScreenshotModal';
import { getCacheOverlaysScreenshots } from '../../../helper/cacheHelper';
import NewMultipleScreenshotModal from './modal/newMultiScreenCaptureAuto';

const MapView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchAddress = location.state?.address || '';
  const fullName = location.state?.fullName || '';
  const email = location.state?.email || '';
  const phone = location.state?.phone || '';
  const selectedProcedure = location.state?.selectedProcedure || '';
  const inputValue = location.state?.inputValue || '';
  const zoneDetailsData = location.state?.zoneDetailsData || '';
  const geoJsonCode = zoneDetailsData?.geoJsonCode || '';

  const [searchParams] = useSearchParams();
  const flow = searchParams.get('flow');

  // Panel collapse state
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  // Local state for component-specific data
  const [error, setError] = useState('');
  const [isPropertyOverlaysModalOpen, setIsPropertyOverlaysModalOpen] = useState(false);
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);
  const [screenshotsCompleted, setScreenshotsCompleted] = useState(false);
  const [capturedScreenshots, setCapturedScreenshots] = useState([]);

  // Get overlay data from store for modal and navigation
  const { overlayOptions, isOverlayLoading, addressData } = useOverlayStore();

  useEffect(() => {
    const cacheOverlayScreenshots = getCacheOverlaysScreenshots();

    if (cacheOverlayScreenshots && Array.isArray(cacheOverlayScreenshots)) {
      // Check if all overlays are uploaded (name === true)
      const allUploaded = cacheOverlayScreenshots.some((item) => item.name === true);

      if (allUploaded) {
        // Filter out the completion marker (name === true)
        const validScreenshots = cacheOverlayScreenshots.filter((item) => item.name !== true);

        setScreenshotsCompleted(true);
        setCapturedScreenshots(validScreenshots);
        setError('');
      }
    }
  }, []); // Empty dependency array - only run once on mount

  const handleNext = () => {
    // Check if there are overlays that need screenshots
    if (overlayOptions.length > 0) {
      // If there are overlays but screenshots are not completed, show error
      if (!screenshotsCompleted) {
        setError('Please complete screenshot capture for all overlays before proceeding.');
        return;
      }
    }

    // Clear any errors and proceed
    setError('');

    // navigate(`/promptSelect${flow ? `?flow=${flow}` : ''}`, {
    //   state: {
    //     searchAddress: searchAddress,
    //     overlayOptions,
    //     screenshots: capturedScreenshots,
    //     fullName,
    //     phone,
    //     email,
    //   },
    // });

    if (flow && flow === 'prelodgement') {
      navigate(`/preLodgementQaChat${flow ? `?flow=${flow}` : ''}`, {
        state: {
          selectedProcedure,
          searchAddress,
          overlays: overlayOptions,
          fullName,
          inputValue,
          email,
          phone,
          zoneDetailsData,
        },
      });
    } else {
      navigate('/generateQuestion', {
        state: {
          selectedProcedure,
          searchAddress,
          overlays: overlayOptions,
          fullName,
          inputValue,
          email,
          phone,
          zoneDetailsData,
        },
      });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  const togglePanel = () => {
    setIsPanelCollapsed(!isPanelCollapsed);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleScreenshotComplete = (success, screenshots) => {
    if (success) {
      setScreenshotsCompleted(true);
      setCapturedScreenshots(screenshots);
      setError('');
    }
  };

  const openScreenshotModal = () => {
    if (overlayOptions.length === 0) {
      setError('No overlays available to capture screenshots.');
      return;
    }
    setIsScreenshotModalOpen(true);
  };

  return (
    <Main role="main" aria-label="Address confirmation page">
      <SectionLeft isCollapsed={isPanelCollapsed}>
        <SliderToggle
          onClick={togglePanel}
          isCollapsed={isPanelCollapsed}
          aria-label={isPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
          className="slider_toggle"
        >
          {isPanelCollapsed ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
        </SliderToggle>

        <PanelContent isCollapsed={isPanelCollapsed}>
          <Heading role="heading" aria-level="1">
            <IntlMessages id="mapview.label.confirm.address" />
          </Heading>
          <SubHeading role="heading" aria-level="2">
            {searchAddress?.address || 'No address provided'}
          </SubHeading>

          {error && (
            <div
              role="alert"
              aria-live="polite"
              style={{
                color: '#d32f2f',
                padding: '10px',
                backgroundColor: '#ffebee',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {screenshotsCompleted && (
            <div
              role="status"
              aria-live="polite"
              style={{
                color: '#2e7d32',
                padding: '8px',
                backgroundColor: '#e8f5e9',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              ✓ Screenshots captured for {capturedScreenshots.length} overlay(s)
            </div>
          )}

          <InfoButton
            onClick={() => setIsPropertyOverlaysModalOpen(true)}
            aria-label="View overlays currently applied to property"
          >
            <span>
              <IntlMessages id="view.property.overlay.list.text" />{' '}
              <Info size={18} aria-hidden="true" color="blue" style={{ marginTop: '5px' }} />
            </span>
          </InfoButton>

          {overlayOptions.length > 0 && (
            <CaptureButton
              onClick={openScreenshotModal}
              disabled={isOverlayLoading || screenshotsCompleted}
              aria-label="Capture screenshots for all overlays"
              style={{
                marginTop: '8px',
                backgroundColor: screenshotsCompleted ? '#e8f5e9' : '#fff3e0',
                borderColor: screenshotsCompleted ? '#4caf50' : '#ff9800',
              }}
              className="capture_screenshot_button"
            >
              <Camera size={18} aria-hidden="true" style={{ marginRight: '8px' }} />
              <span>
                {screenshotsCompleted ? 'Screenshots Captured' : 'Capture the Overlay Screenshots'}
              </span>
            </CaptureButton>
          )}

          <ButtonWapper>
            <Button onClick={handleBack} aria-label="Go back to previous step">
              <ArrowLeft /> <IntlMessages id="button.label.back" />
            </Button>
            <Button
              onClick={handleNext}
              disabled={isOverlayLoading}
              aria-busy={isOverlayLoading}
              aria-label="Proceed to next step"
            >
              {isOverlayLoading ? (
                <IntlMessages id="button.label.loading" />
              ) : (
                <IntlMessages id="button.label" />
              )}
            </Button>
          </ButtonWapper>
        </PanelContent>
      </SectionLeft>

      <SectionRight isCollapsed={isPanelCollapsed}>
        <MapWithOverlays
          searchAddress={searchAddress}
          onError={handleError}
          showDropdown={true}
          key={screenshotsCompleted}
          geoJsonCode={geoJsonCode}
        />
      </SectionRight>

      <PropertyOverlaysModal
        isModalOpen={isPropertyOverlaysModalOpen}
        onClose={() => setIsPropertyOverlaysModalOpen(false)}
        addressOverlays={overlayOptions}
        isFetching={isOverlayLoading}
      />

      <NewMultipleScreenshotModal
        isOpen={isScreenshotModalOpen}
        onClose={() => setIsScreenshotModalOpen(false)}
        searchAddress={addressData || searchAddress}
        overlayOptions={overlayOptions}
        onComplete={handleScreenshotComplete}
        geoJsonCode={geoJsonCode}
      />
    </Main>
  );
};

export default MapView;
